import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, categorySlugs } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

const catArticleImages: Record<string, string> = {
  'article-bt-guide-2025': 'https://readdy.ai/api/search-image?query=Warm%20toned%20modern%20digital%20technology%20abstract%20with%20soft%20gradient%20and%20geometric%20patterns%2C%20minimalist%20editorial%20style%2C%20clean%20composition%2C%20cinematic%20depth%20of%20field%2C%20no%20text%20no%20human%20faces&width=800&height=500&seq=cat-bt-guide&orientation=landscape',
  'article-magnet-vs-torrent': 'https://readdy.ai/api/search-image?query=Abstract%20network%20connection%20nodes%20with%20warm%20amber%20and%20terracotta%20gradient%20background%2C%20modern%20digital%20technology%20concept%2C%20minimalist%20geometric%20style%2C%20soft%20lighting%2C%20no%20text%20no%20human%20faces&width=800&height=500&seq=cat-magnet&orientation=landscape',
  'article-summer-movies': 'https://readdy.ai/api/search-image?query=Cinematic%20film%20reel%20abstract%20with%20warm%20golden%20light%20and%20soft%20bokeh%2C%20elegant%20movie%20concept%2C%20artistic%20depth%20of%20field%2C%20no%20text%20no%20human%20faces&width=800&height=500&seq=cat-movies&orientation=landscape',
  'article-lossless-music': 'https://readdy.ai/api/search-image?query=Abstract%20sound%20wave%20frequency%20visualization%20with%20warm%20golden%20gradient%2C%20elegant%20audio%20spectrum%20lines%2C%20modern%20minimalist%20design%2C%20soft%20lighting%2C%20no%20text%20no%20human%20faces&width=800&height=500&seq=cat-music&orientation=landscape',
  'article-open-source': 'https://readdy.ai/api/search-image?query=Modern%20digital%20code%20and%20software%20development%20abstract%20concept%2C%20warm%20terracotta%20and%20amber%20gradient%2C%20geometric%20and%20clean%20composition%2C%20minimalist%20style%2C%20no%20text%20no%20human%20faces&width=800&height=500&seq=cat-opensource&orientation=landscape',
  'article-indie-games': 'https://readdy.ai/api/search-image?query=Abstract%20game%20controller%20and%20pixel%20art%20concept%20with%20warm%20golden%20gradient%2C%20modern%20gaming%20vibe%2C%20minimalist%20geometric%20composition%2C%20soft%20lighting%2C%20no%20text%20no%20human%20faces&width=800&height=500&seq=cat-games&orientation=landscape',
  'article-ebook-recommend': 'https://readdy.ai/api/search-image?query=Abstract%20open%20book%20with%20digital%20pages%20concept%2C%20warm%20amber%20and%20golden%20gradient%20background%2C%20modern%20minimalist%20knowledge%20theme%2C%20soft%20lighting%2C%20no%20text%20no%20human%20faces&width=800&height=500&seq=cat-ebook&orientation=landscape',
  'article-safety-guide': 'https://readdy.ai/api/search-image?query=Abstract%20shield%20and%20digital%20security%20concept%20with%20warm%20terracotta%20gradient%2C%20modern%20minimalist%20cybersecurity%20theme%2C%20geometric%20clean%20composition%2C%20no%20text%20no%20human%20faces&width=800&height=500&seq=cat-safety&orientation=landscape',
};

const defaultCatImage = 'https://readdy.ai/api/search-image?query=Warm%20toned%20modern%20digital%20technology%20abstract%20with%20soft%20gradient%20and%20geometric%20patterns%2C%20minimalist%20editorial%20style%2C%20clean%20composition%2C%20cinematic%20depth%20of%20field%2C%20no%20text%20no%20human%20faces&width=800&height=500&seq=cat-default&orientation=landscape';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const articles = getArticlesByCategory(currentCategory.slug);

  const [activeCategory, setActiveCategory] = useState(currentCategory.slug);

  useEffect(() => {
    if (slug && categorySlugs.includes(slug)) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    navigate(`/category/${newSlug}`);
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 种子搜索`,
    description: currentCategory.description,
    url: `https://gdjsfzyxgs.cn/category/${currentCategory.slug}`,
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://gdjsfzyxgs.cn/' },
      { '@type': 'ListItem', position: 2, name: '资源分类', item: 'https://gdjsfzyxgs.cn/category/movie' },
      { '@type': 'ListItem', position: 3, name: currentCategory.name },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 种子搜索 | BT磁力链接与种子文件资源`}
        description={currentCategory.description}
        keywords={`${currentCategory.keywords.join(',')},种子搜索,BT种子搜索,磁力链接搜索`}
        canonicalUrl={`https://gdjsfzyxgs.cn/category/${currentCategory.slug}`}
        schemaJson={{ ...pageSchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '资源分类', path: '/category/movie' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-500">
              {currentCategory.description}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200/60 transition-all duration-300"
                >
                  <div className="relative h-44 md:h-48 overflow-hidden">
                    <img
                      src={catArticleImages[article.imageSeq] || defaultCatImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 text-xs font-medium rounded-md bg-accent-500 text-white">
                        {article.categoryName}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <h2 className="text-sm md:text-base font-semibold font-heading text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.date}</span>
                      <span>{article.readTime}阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章，请浏览其他分类</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}